import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AmbassadorBoard } from "@/components/genesis/AmbassadorBoard";
import { ArrowLeft, ExternalLink, Wallet, FileSearch, Gift, Check, Clock, Share2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { COMPANY } from "@/lib/company";
import { PhoneLink } from "@/components/app/PhoneLink";
import { useAuth } from "@/context/AuthContext";
import { useGenesis } from "@/hooks/useGenesis";
import { genesisShareUrl, shareLink } from "@/lib/api";
import { StatusChip, Countdown, ProgressBar, VerifiedTag, MintCopy, BuyButton, RiskNote, fmtSol, fmtNum, fmtDate } from "@/components/genesis/GenesisBits";
import { GenesisToken } from "@/components/genesis/GenesisToken";
import { cn } from "@/lib/utils";

const GenesisGuide = lazy(() => import("@/components/genesis/GenesisGuide"));

function HowItWorksButton({ onClick, className, testid = "genesis-how-button" }) {
  return <button onClick={onClick} data-testid={testid} className={cn("inline-flex items-center justify-center gap-2 rounded-full border border-am-border-strong px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white", className)}><BookOpen className="h-4 w-4" /> How It Works</button>;
}

const STEPS = [
  { icon: FileSearch, title: "Review the terms", body: "Read the sale terms here and on the official provider page. Only contribute what you fully understand and can afford to lose." },
  { icon: Wallet, title: "Contribute on Smithii", body: "The sale runs on Smithii, the official provider. There you connect your own wallet and contribute SOL within the per-wallet limits during the sale window." },
  { icon: Gift, title: "Claim after the sale", body: "When the sale ends, tokens are claimed on Smithii according to its rules. AuraMax only displays information." },
];
const LIVE = ["Profiles & personal brands", "Content & communities", "Solana wallet linking with real balances"];
const PLANNED = ["Creator monetization", "Token utility", "Personal coins"];

export default function Genesis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const g = useGenesis();
  const [guideOpen, setGuideOpen] = useState(false);
  const [params] = useSearchParams();
  const [sharedBy, setSharedBy] = useState(null);
  useEffect(() => {
    api.genesisTrack({ action: "view", surface: "page", via: params.get("via") || undefined }).then((r) => setSharedBy(r?.ambassador || null)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  const heroCtaRef = useRef(null);
  const detailsRef = useRef(null);
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => setHeroCtaVisible(e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [g.data?.purchase_link_enabled]);
  const openGuide = () => { setGuideOpen(true); api.genesisTrack({ action: "how_open", surface: "page" }).catch(() => {}); };
  const review = () => detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div className={cn("text-white", !user && "min-h-screen bg-am-bg")} data-testid="genesis-page">
      {!user && (
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-am-border bg-am-bg/90 px-4 py-3 backdrop-blur-xl">
          <button onClick={() => navigate("/")} data-testid="genesis-public-logo" className="flex items-center gap-2"><Logo size={30} /><span className="font-mark text-base font-extrabold tracking-tight">AURAMAX</span></button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/login")} data-testid="genesis-public-login" className="rounded-full px-3 py-1.5 text-sm font-semibold text-am-text2 hover:text-white">Log in</button>
            <button onClick={() => navigate("/join")} data-testid="genesis-public-join" className="rounded-full aura-gradient px-4 py-1.5 text-sm font-bold text-black">Join AuraMax</button>
          </div>
        </header>
      )}
      <main className="mx-auto max-w-3xl px-4 py-5 pb-32 md:px-6 md:pb-24">
        {user && <button onClick={() => navigate(-1)} data-testid="genesis-back" className="mb-4 flex items-center gap-1 text-sm text-am-text2 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>}
        {sharedBy && <div data-testid="genesis-shared-by" className="mb-3 inline-flex items-center gap-2 rounded-full border border-am-border bg-am-surface px-3 py-1 text-xs text-am-text2">Shared by <Link to={`/@${sharedBy.username}`} className="font-semibold text-white hover:underline">@{sharedBy.username}</Link></div>}
        <Hero g={g} onHow={openGuide} ctaRef={heroCtaRef} />
        <Status g={g} />
        <div ref={detailsRef} className="scroll-mt-20"><Details g={g} /></div>
        <HowItWorks g={g} onHow={openGuide} />
        <Vision />
        <Transparency g={g} />
        <AmbassadorBoard />
        <FinalCta g={g} />
        <p className="mt-8 text-center text-xs leading-relaxed text-am-text3" data-testid="genesis-disclaimer">This page is informational only and is not financial advice. Participation involves risk, including total loss. No returns are guaranteed. Eligibility depends on your jurisdiction. Purchases and claims happen on the official provider, never inside AuraMax. <Link to="/terms" data-testid="genesis-terms-link" className="underline hover:text-white">Terms</Link> · <Link to="/privacy" data-testid="genesis-privacy-link" className="underline hover:text-white">Privacy</Link></p>
        <div className="mt-6 flex flex-col items-center gap-1 border-t border-am-border pt-5 text-xs text-am-text3" data-testid="genesis-company-contact">
          <span className="font-bold uppercase tracking-wide">Official Company Contact</span>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1"><span>{COMPANY.legalName}</span><PhoneLink testid="genesis-company-phone" className="text-xs" /><Link to="/contact" data-testid="genesis-company-contact-link" className="hover:text-white">Contact page</Link></div>
        </div>
      </main>
      {!heroCtaVisible && <StickyBuyBar g={g} loggedIn={!!user} />}
      {guideOpen && <Suspense fallback={null}><GenesisGuide open={guideOpen} onOpenChange={setGuideOpen} data={g.data} status={g.status} onReview={review} /></Suspense>}
    </div>
  );
}

function StickyBuyBar({ g, loggedIn }) {
  const { data, status } = g;
  if (!data?.purchase_link_enabled || status === "ended" || !status) return null;
  return (
    <div data-testid="genesis-sticky-buy" className={cn("fixed inset-x-0 z-30 px-3 md:hidden", loggedIn ? "bottom-16" : "bottom-3")}>
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-am-border-strong bg-am-bg/90 p-2 pl-3 shadow-2xl backdrop-blur-xl">
        <div className="min-w-0 flex-1"><div className="truncate text-xs font-bold text-white">AURAMAX GENESIS</div><div className="mt-0.5"><StatusChip status={status} /></div></div>
        <BuyButton data={data} status={status} size="sm" testid="genesis-sticky-buy-btn" surface="sticky" className="shrink-0" />
      </div>
    </div>
  );
}

function FinalCta({ g }) {
  const { data, status } = g;
  if (!data?.purchase_link_enabled || status === "ended") return null;
  return (
    <section data-testid="genesis-final-cta" className="relative mt-6 overflow-hidden rounded-3xl border border-am-border bg-am-surface p-6 text-center sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 -bottom-24 h-48 aura-radial opacity-20 blur-3xl" />
      <div className="relative">
        <h2 className="font-display text-2xl font-extrabold text-white sm:text-3xl">Ready to join Genesis?</h2>
        <p className="mt-2 text-sm text-am-text2">The sale runs on {data.provider.name}, the official provider. Connect your own wallet there and contribute within the per-wallet limits.</p>
        <div className="mt-5 flex justify-center"><BuyButton data={data} status={status} size="lg" testid="genesis-buy-bottom" surface="bottom" /></div>
        <RiskNote className="mx-auto mt-3 max-w-md" />
      </div>
    </section>
  );
}

function Hero({ g, onHow, ctaRef }) {
  const { data, status, countdown } = g;
  return (
    <section className="relative overflow-hidden rounded-3xl border border-am-border bg-am-surface/60 p-6 sm:p-10 animate-[fadeIn_.5s_ease]">
      <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full aura-radial opacity-25 blur-3xl" />
      <div className="relative grid items-center gap-6 sm:grid-cols-[auto_1fr]">
        <div className="mx-auto"><GenesisToken size={160} /></div>
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center gap-2 sm:justify-start"><StatusChip status={status} /><Countdown countdown={countdown} status={status} compact /></div>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">AURAMAX GENESIS</h1>
          <p className="mt-2 text-base font-semibold aura-text md:text-lg">The next era of social media.</p>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-am-text2">AuraMax is building the social network for personal brands. Powered by Solana, $AURAMAX is designed to support creator monetization, token-powered features, and a future personal coin economy.</p>
          <div className="mt-5 flex flex-col items-center gap-3 sm:items-start">
            <div ref={ctaRef} className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
              <BuyButton data={data} status={status} size="lg" testid="genesis-buy-top" surface="hero" className="w-full sm:w-auto" />
              <HowItWorksButton onClick={onHow} testid="genesis-how-button" className="w-full sm:w-auto py-3.5" />
            </div>
            {data?.purchase_link_enabled && status !== "ended" && <RiskNote className="max-w-md text-center sm:text-left" />}
          </div>
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-am-text3">One identity. Limitless potential. Max your aura.</p>
        </div>
      </div>
    </section>
  );
}

function Status({ g }) {
  const { user } = useAuth();
  const { data, status, countdown } = g;
  const share = async () => {
    const url = genesisShareUrl(user?.username);
    const r = await shareLink(url, "AURAMAX GENESIS");
    if (r === "copied") toast(user ? "Your personal Genesis link is copied" : "Link copied"); else if (r === "failed") toast(url);
  };
  return (
    <section data-testid="genesis-status-section" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-white md:text-lg">Sale status</h2>
        <div className="flex items-center gap-2">
          <StatusChip status={status} />
          <button onClick={share} data-testid="genesis-share" className="inline-flex items-center gap-1.5 rounded-full border border-am-border-strong px-3 py-1 text-xs font-semibold text-white hover:bg-white/5"><Share2 className="h-3.5 w-3.5" /> Share</button>
        </div>
      </div>
      {!data ? <p className="mt-2 text-sm text-am-text3">Loading…</p>
        : status === "unavailable" ? <p className="mt-2 text-sm text-am-text2" data-testid="genesis-status-unavailable">Status unavailable — see the official page for the current state of the sale.</p>
        : status === "ended" ? <p className="mt-2 text-sm text-am-text2" data-testid="genesis-status-ended">{data.progress ? `The Genesis sale has ended · ${fmtSol(data.progress.sold_sol)} raised.` : "The Genesis sale has ended. Final figures are unavailable."}</p>
        : (
          <div className="mt-3 space-y-3">
            <Countdown countdown={countdown} status={status} />
            <ProgressBar progress={data.progress} />
            <div className="flex items-center gap-2 text-xs text-am-text2"><Clock className="h-3.5 w-3.5" /> {fmtDate(data.terms.start_at)} → {fmtDate(data.terms.end_at)}</div>
          </div>
        )}
      {data && <div className="mt-3"><VerifiedTag data={data} /></div>}
      {data && <div className="mt-4 flex flex-wrap items-center gap-3"><BuyButton data={data} status={status} size="md" testid="genesis-page-official-link" surface="status" /></div>}
    </section>
  );
}

function Details({ g }) {
  const { data } = g;
  if (!data) return null;
  const t = data.terms;
  const rows = [
    ["Payment", data.payment], ["Soft cap", fmtSol(t.soft_cap_sol)], ["Hard cap", fmtSol(t.hard_cap_sol)],
    ["Min per wallet", fmtSol(t.min_sol)], ["Max per wallet", fmtSol(t.max_sol)],
    ["Sale rate", `${fmtNum(t.sale_rate)} $AURAMAX per SOL`], ["Listing rate", `${fmtNum(t.listing_rate)} $AURAMAX per SOL`],
    ["Total supply", fmtNum(t.supply, 0)], ["Decimals", String(t.decimals)], ["Start", fmtDate(t.start_at)], ["End", fmtDate(t.end_at)],
  ];
  return (
    <section data-testid="genesis-details" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-base font-bold text-white md:text-lg">Presale details</h2><VerifiedTag data={data} /></div>
      <div className="mt-3 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        {rows.map(([k, v]) => <div key={k} className="flex items-center justify-between gap-3 border-b border-am-border py-2 text-sm"><span className="text-am-text3">{k}</span><span className="text-right font-semibold text-white">{v}</span></div>)}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2"><span className="text-sm text-am-text3">Token address</span><MintCopy mint={data.mint} full /></div>
    </section>
  );
}

function HowItWorks({ g, onHow }) {
  const name = g.data?.provider?.name || "Smithii";
  return (
    <section data-testid="genesis-how" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5">
      <h2 className="text-base font-bold text-white md:text-lg">How it works</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.title} className="rounded-2xl border border-am-border bg-am-bg/50 p-4">
            <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-am-surface3"><s.icon className="h-4 w-4 text-aura-cyan" /></span><span className="text-xs font-bold text-am-text3">STEP {i + 1}</span></div>
            <h3 className="mt-2 text-sm font-bold text-white">{s.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-am-text2">{s.body}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-am-text2">Purchases and claims happen on {name}, the official provider — never inside AuraMax. AuraMax never asks for your keys or a wallet signature to view this page.</p>
      <div className="mt-4"><HowItWorksButton onClick={onHow} testid="genesis-how-button-section" /><span className="ml-3 text-xs text-am-text3">Beginner walkthrough · 10 short steps · no wallet needed</span></div>
    </section>
  );
}

function Vision() {
  const Col = ({ title, items, testid }) => (
    <div data-testid={testid}>
      <h3 className="text-xs font-bold uppercase tracking-wide text-am-text3">{title}</h3>
      <ul className="mt-2 space-y-1.5">{items.map((x) => <li key={x} className="flex items-center gap-2 text-sm text-white"><Check className="h-3.5 w-3.5 shrink-0 text-aura-mint" />{x}</li>)}</ul>
    </div>
  );
  return (
    <section data-testid="genesis-vision" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5">
      <h2 className="text-base font-bold text-white md:text-lg">Project vision</h2>
      <div className="mt-3 grid gap-5 sm:grid-cols-2"><Col title="Live today" items={LIVE} testid="genesis-vision-live" /><Col title="Planned" items={PLANNED} testid="genesis-vision-planned" /></div>
    </section>
  );
}

function Transparency({ g }) {
  const { data } = g;
  if (!data) return null;
  const notPublished = <span className="text-am-text2">Not published by the provider</span>;
  const rows = [
    ["Total supply", fmtNum(data.terms.supply, 0)], ["Sale terms", "See presale details above"],
    ["Liquidity", data.liquidity_percent != null ? `${data.liquidity_percent}% (per provider record)` : notPublished],
    ["Liquidity lock", notPublished], ["Vesting", notPublished], ["Supply distribution", notPublished],
    ["Risk", "You can lose your entire contribution"], ["Eligibility", "Depends on your jurisdiction — check local rules before participating"],
  ];
  return (
    <section data-testid="genesis-transparency" className="mt-4 rounded-3xl border border-am-border bg-am-surface p-5">
      <h2 className="text-base font-bold text-white md:text-lg">Transparency</h2>
      <div className="mt-3 divide-y divide-am-border">{rows.map(([k, v]) => <div key={k} className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"><span className="text-am-text3">{k}</span><span className="font-semibold text-white sm:text-right">{v}</span></div>)}</div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-2"><span className="text-am-text3">Official mint</span><MintCopy mint={data.mint} full /></div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <a href={data.provider.url} target="_blank" rel="noopener noreferrer" data-testid="genesis-provider-link" className="inline-flex items-center gap-1 text-aura-cyan hover:underline">Official provider page ({data.provider.name}) <ExternalLink className="h-3.5 w-3.5" /></a>
          <a href={data.x_url} target="_blank" rel="noopener noreferrer" data-testid="genesis-x-link" className="inline-flex items-center gap-1 text-aura-cyan hover:underline">Official X account <ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
      </div>
    </section>
  );
}
